// ============================================================================
// Shopee — HMAC-SHA256 request signing.
//
// Every shop-scoped Shopee Open Platform call is signed with a partner-level
// HMAC. The base string is:
//
//     {partner_id}{api_path}{timestamp}{access_token}{shop_id}
//
// (concatenated, no separators). We HMAC-SHA256 it with the `partner_key` and
// encode to LOWERCASE hex. Shopee then verifies by re-running the same HMAC
// on its side — any drift in the base string (extra whitespace, wrong path,
// misordered fields) produces `error_sign`.
//
// Public / partner-level endpoints use a shorter base string:
//
//     {partner_id}{api_path}{timestamp}
//
// (no access_token + no shop_id, used for `/api/v2/shop/auth_partner`,
// `/api/v2/auth/token/get`, and `/api/v2/auth/access_token/get`).
//
// ALL callers MUST put `partner_id`, `timestamp`, `sign`, `shop_id`, and
// `access_token` in the URL QUERY STRING — Shopee ignores Authorization
// headers. See `client.ts` for the SECURITY note on access_token in URL.
// ============================================================================

import { hmac, bytesToHex } from '../../services/webhook-verifier'
import { ShopeeError } from './types'

// ============================================================================
// Constants
// ============================================================================

/** Maximum clock drift we accept between local time and the timestamp we send. */
export const SHOPEE_MAX_CLOCK_DRIFT_MS = 5 * 60 * 1000

// ============================================================================
// hmacSha256Hex — shared helper
// ============================================================================

/**
 * Shared low-level helper: `HMAC-SHA256(key, message)` → lowercase hex.
 *
 * Reuses `hmac(...)` from the connectors webhook-verifier service for
 * consistency with every other adapter — the same primitive that verifies
 * inbound FB / Line / IG webhooks is used to SIGN outbound Shopee requests.
 */
export async function hmacSha256Hex(key: string, message: string): Promise<string> {
  const encoded = new TextEncoder().encode(message)
  const digest = await hmac(key, encoded, 'SHA-256')
  return bytesToHex(digest)
}

// ============================================================================
// assertClockSane — pre-flight drift check
// ============================================================================

/**
 * Throw `ShopeeError('CLOCK_DRIFT')` if our local clock differs from Shopee's
 * expected window (±5 minutes **INCLUSIVE**) so we don't burn a round trip +
 * signing cycle just to learn the timestamp was outside the accepted window.
 *
 * Boundary semantics (Issue #255 L1): the check is `drift > MAX_DRIFT`
 * (strict `>`), so a drift of EXACTLY 5:00.000 passes. Callers treating this
 * as `[-5min, +5min]` inclusive is correct. Only strictly-greater drifts
 * throw.
 *
 * The optional `shopeeNowMs` lets the ingress / scheduler pipelines pass the
 * server's `Date` response header from an earlier call — when we have a
 * trusted clock reference, drift is computed against it directly.
 */
export function assertClockSane(nowMs: number, shopeeNowMs?: number): void {
  if (typeof nowMs !== 'number' || !Number.isFinite(nowMs)) {
    throw new ShopeeError('INVALID_INPUT', 'assertClockSane: nowMs must be a finite number')
  }
  if (typeof shopeeNowMs === 'number' && Number.isFinite(shopeeNowMs)) {
    const drift = Math.abs(nowMs - shopeeNowMs)
    if (drift > SHOPEE_MAX_CLOCK_DRIFT_MS) {
      throw new ShopeeError(
        'CLOCK_DRIFT',
        `local clock differs from Shopee by ${Math.round(drift / 1000)}s (max ${SHOPEE_MAX_CLOCK_DRIFT_MS / 1000}s)`,
      )
    }
  }
  // Sanity check: year 2000+ reject absurdly off-epoch timestamps that point
  // to a broken clock (e.g. uninitialised, stuck at 0, overflowed negative).
  const MIN_SANE_MS = 946684800000 // 2000-01-01
  const MAX_SANE_MS = 4102444800000 // 2100-01-01
  if (nowMs < MIN_SANE_MS || nowMs > MAX_SANE_MS) {
    throw new ShopeeError(
      'CLOCK_DRIFT',
      `local clock ${new Date(nowMs).toISOString()} is outside the sane range`,
    )
  }
}

// ============================================================================
// signShopeeRequest — shop-scoped signing (the common path)
// ============================================================================

export interface SignShopeeRequestInput {
  partnerId: string | number
  partnerKey: string
  apiPath: string
  accessToken: string
  shopId: string | number
  /** Override timestamp for deterministic tests. Defaults to `floor(Date.now()/1000)`. */
  timestampSec?: number
}

export interface SignShopeeRequestOutput {
  sign: string
  timestamp: number
  /** URL query parameters the caller MUST include verbatim. */
  queryParams: Record<string, string>
}

/**
 * Build the Shopee HMAC-SHA256 signature for a shop-scoped API call.
 *
 * Base string: `{partnerId}{apiPath}{timestamp}{accessToken}{shopId}`.
 * Signed with `partnerKey` → lowercase hex.
 *
 * Returned `queryParams` is the full set of auth params the URL must carry:
 * `partner_id`, `timestamp`, `access_token`, `shop_id`, `sign`. Callers
 * merge any endpoint-specific query on top before issuing the request.
 */
export async function signShopeeRequest(
  input: SignShopeeRequestInput,
): Promise<SignShopeeRequestOutput> {
  assertNonEmpty(input.partnerId, 'partnerId')
  assertNonEmpty(input.partnerKey, 'partnerKey')
  assertNonEmpty(input.apiPath, 'apiPath')
  assertNonEmpty(input.accessToken, 'accessToken')
  assertNonEmpty(input.shopId, 'shopId')

  const timestamp =
    typeof input.timestampSec === 'number'
      ? Math.floor(input.timestampSec)
      : Math.floor(Date.now() / 1000)

  // Pre-flight drift check using the resolved timestamp in ms.
  assertClockSane(timestamp * 1000)

  const partnerIdStr = String(input.partnerId)
  const shopIdStr = String(input.shopId)
  const baseString = `${partnerIdStr}${input.apiPath}${timestamp}${input.accessToken}${shopIdStr}`
  const sign = await hmacSha256Hex(input.partnerKey, baseString)

  return {
    sign,
    timestamp,
    queryParams: {
      partner_id: partnerIdStr,
      timestamp: String(timestamp),
      access_token: input.accessToken,
      shop_id: shopIdStr,
      sign,
    },
  }
}

// ============================================================================
// signShopeePublicRequest — partner-scoped (no shop + no token)
// ============================================================================

export interface SignShopeePublicRequestInput {
  partnerId: string | number
  partnerKey: string
  apiPath: string
  timestampSec?: number
}

export interface SignShopeePublicRequestOutput {
  sign: string
  timestamp: number
  queryParams: Record<string, string>
}

/**
 * Partner-level signature — used for auth endpoints (authorize URL build,
 * code exchange, refresh). Base string is shorter:
 * `{partnerId}{apiPath}{timestamp}` — no token + no shop id.
 */
export async function signShopeePublicRequest(
  input: SignShopeePublicRequestInput,
): Promise<SignShopeePublicRequestOutput> {
  assertNonEmpty(input.partnerId, 'partnerId')
  assertNonEmpty(input.partnerKey, 'partnerKey')
  assertNonEmpty(input.apiPath, 'apiPath')

  const timestamp =
    typeof input.timestampSec === 'number'
      ? Math.floor(input.timestampSec)
      : Math.floor(Date.now() / 1000)

  assertClockSane(timestamp * 1000)

  const partnerIdStr = String(input.partnerId)
  const baseString = `${partnerIdStr}${input.apiPath}${timestamp}`
  const sign = await hmacSha256Hex(input.partnerKey, baseString)

  return {
    sign,
    timestamp,
    queryParams: {
      partner_id: partnerIdStr,
      timestamp: String(timestamp),
      sign,
    },
  }
}

// ============================================================================
// Helpers
// ============================================================================

function assertNonEmpty(value: string | number | undefined | null, field: string): void {
  if (value === undefined || value === null) {
    throw new ShopeeError('INVALID_INPUT', `${field} is required`)
  }
  if (typeof value === 'string' && value.length === 0) {
    throw new ShopeeError('INVALID_INPUT', `${field} must be a non-empty string`)
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new ShopeeError('INVALID_INPUT', `${field} must be a finite number`)
  }
}
